import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { settingsApi } from "../api/endpoints";
import type { UserSettings } from "../types/domain";

export function useSettings() {
  const qc = useQueryClient();
  const query = useQuery({
    queryKey: ["settings"],
    queryFn: async () => (await settingsApi.get()).settings,
    staleTime: 60_000,
  });

  const mutate = useMutation({
    mutationFn: (patch: Partial<UserSettings>) => settingsApi.patch(patch),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: ["settings"] });
      const prev = qc.getQueryData<UserSettings>(["settings"]);
      if (prev) qc.setQueryData(["settings"], { ...prev, ...patch });
      return { prev };
    },
    onError: (_err, _patch, ctx) => {
      if (ctx?.prev) qc.setQueryData(["settings"], ctx.prev);
    },
    onSuccess: (res) => qc.setQueryData(["settings"], res.settings),
  });

  // Apply theme/density to <body> whenever they change.
  useEffect(() => {
    if (!query.data) return;
    document.body.dataset.theme = query.data.theme;
    document.body.dataset.density = query.data.density;
  }, [query.data?.theme, query.data?.density]);

  return { settings: query.data, isLoading: query.isLoading, update: mutate.mutate };
}
